import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PaperPlaneRightIcon, PlusIcon, ChatCircleDotsIcon, SparkleIcon, MicrophoneIcon, WaveformIcon } from "@phosphor-icons/react";

// Minimal markdown renderer (headings, lists, bold, code, paragraphs)
export function renderChatMarkdown(md) {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  const flushList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  for (const raw of lines) {
    const line = raw.replace(/</g, "&lt;");
    const inline = line
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/`([^`]+)`/g, "<code>$1</code>");
    if (/^##\s+/.test(line)) { flushList(); out.push(`<h3 class="chat-h">${inline.replace(/^##\s+/, "")}</h3>`); }
    else if (/^#\s+/.test(line)) { flushList(); out.push(`<h2 class="chat-h">${inline.replace(/^#\s+/, "")}</h2>`); }
    else if (/^[-*]\s+/.test(line)) {
      if (!inList) { out.push('<ul class="chat-ul">'); inList = true; }
      out.push(`<li>${inline.replace(/^[-*]\s+/, "")}</li>`);
    } else if (line.trim() === "") { flushList(); out.push(""); }
    else { flushList(); out.push(`<p>${inline}</p>`); }
  }
  flushList();
  return out.join("\n");
}

export default function ChatConversation({ conversation, onTitleChange, compact = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const cancelRef = useRef(false);

  const load = async () => {
    if (!conversation?._id) return;
    try {
      const { data } = await api.get(`/chat/conversations/${conversation._id}/messages`);
      setMessages(data);
    } catch (e) { console.warn("ChatConversation: failed to load messages", e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [conversation?._id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || !conversation?._id) return;
    setSending(true);
    setInput("");
    // Optimistic push
    setMessages((m) => [...m, { role: "user", content: text, created_at: new Date().toISOString(), _id: "tmp-" + Date.now() }]);
    try {
      const { data } = await api.post(`/chat/conversations/${conversation._id}/messages`, { content: text });
      setMessages((m) => [...m.filter((x) => !String(x._id).startsWith("tmp-")), data.user_message, data.assistant_message]);
      if (onTitleChange) onTitleChange();
    } catch (e) {
      // ai-chat throws plain Errors (no_api_key / invalid_api_key / etc.)
      // with the real message on e.message, not e.response.data.detail —
      // fall back to the Error itself so formatApiErrorDetail can read it.
      toast.error(formatApiErrorDetail(e?.response?.data?.detail || e));
      setMessages((m) => m.filter((x) => !String(x._id).startsWith("tmp-")));
    } finally { setSending(false); }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (recording || transcribing || sending) return;
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Microphone not supported in this browser");
      return;
    }
    try {
      cancelRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stopStream();
        if (cancelRef.current) return;
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 1000) { toast.info("Too short — hold longer"); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const { data } = await api.post("/chat/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data?.text) {
            setInput((v) => (v ? v + " " : "") + data.text);
          } else {
            toast.info("Didn't catch that — try again");
          }
        } catch (e) {
          toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setTranscribing(false); }
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error(e.name === "NotAllowedError" ? "Microphone permission denied" : "Couldn't start recording");
      stopStream();
    }
  };

  const stopRecording = (cancel = false) => {
    cancelRef.current = cancel;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch (e) { console.warn("Recorder stop failed", e); }
    } else {
      stopStream();
    }
    setRecording(false);
  };

  useEffect(() => () => stopStream(), []);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div ref={scrollRef} className={`flex-1 min-h-0 overflow-y-auto ${compact ? "px-3 py-3" : "px-6 py-6"} space-y-4`} data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-sm text-zinc-500 flex flex-col items-center justify-center h-full text-center gap-2 py-10">
            <SparkleIcon size={22} weight="fill" className="text-[#0176D3]" />
            <div className="font-display font-bold text-lg tracking-tight text-zinc-900">Ask AI anything</div>
            <div className="max-w-md text-xs">
              Try: "What are my top blockers?" · "Who should own the schema task?" · "Summarise Warehouse Management for me"
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m._id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
            {m.role === "user" ? (
              <div className={`max-w-[80%] bg-[#0176D3] text-white rounded-sm px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap`}>
                {m.content}
              </div>
            ) : (
              <div className={`max-w-[85%] border border-border bg-white rounded-sm px-3 py-2`}>
                <div className="flex items-center gap-1 mb-1">
                  <SparkleIcon size={10} weight="fill" className="text-[#0176D3]" />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Claude Sonnet 4.5</span>
                </div>
                <div className="text-sm leading-relaxed chat-md" dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderChatMarkdown(m.content)) }} />
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="chat-typing">
            <div className="border border-border bg-white rounded-sm px-3 py-2 text-xs text-zinc-500 inline-flex items-center gap-2">
              <span className="inline-flex gap-0.5">
                <span className="w-1 h-1 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1 h-1 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1 h-1 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "300ms" }} />
              </span>
              Thinking…
            </div>
          </div>
        )}
      </div>
      <div className={`border-t border-border ${compact ? "p-2" : "p-4"} bg-white flex items-center gap-2`}>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={sending || recording || transcribing || !conversation?._id}
          placeholder={
            recording ? "Listening… release to transcribe" :
            transcribing ? "Transcribing…" :
            (conversation ? "Ask about projects, tasks, teammates…" : "Create a new chat first")
          }
          className="rounded-sm"
          data-testid="chat-input"
        />
        <button
          onMouseDown={startRecording}
          onMouseUp={() => stopRecording(false)}
          onMouseLeave={() => { if (recording) stopRecording(true); }}
          onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
          onTouchEnd={(e) => { e.preventDefault(); stopRecording(false); }}
          disabled={sending || transcribing || !conversation?._id}
          data-testid="chat-mic"
          title="Press and hold to speak"
          aria-label="Press and hold to speak"
          className={`h-9 w-9 shrink-0 rounded-sm border flex items-center justify-center transition-colors select-none ${
            recording
              ? "bg-red-600 text-white border-red-600 animate-pulse"
              : transcribing
                ? "bg-zinc-100 text-zinc-500 border-zinc-200 cursor-wait"
                : "bg-white text-zinc-700 border-border hover:bg-zinc-50 active:bg-zinc-100"
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {transcribing ? <WaveformIcon size={14} weight="fill" /> : <MicrophoneIcon size={14} weight={recording ? "fill" : "regular"} />}
        </button>
        <Button onClick={send} disabled={sending || recording || transcribing || !input.trim() || !conversation?._id} data-testid="chat-send" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
          <PaperPlaneRightIcon size={14} weight="fill" />
        </Button>
      </div>
    </div>
  );
}
